import { useState, useEffect, useRef } from 'preact/hooks';
import { ChevronDown } from 'lucide-preact';
import '../../css/chartDropdown.css';

export interface ChartDropdownOption {
  id: string;
  label: string;
}

export interface ChartDropdownProps {
  options: ChartDropdownOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  className?: string;
}

export const ChartDropdown = ({
  options,
  selectedId,
  onSelect,
  className = ''
}: ChartDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find(opt => opt.id === selectedId) || options[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (id: string) => {
    onSelect(id);
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className={`chart-dropdown-container ${className}`}
    >
      <button
        type="button"
        className="chart-dropdown-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="chart-dropdown-label">{selectedOption.label}</span>
        <ChevronDown
          size={14}
          className={`chart-dropdown-icon ${isOpen ? 'open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="chart-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chart-dropdown-item ${selectedId === option.id ? 'active' : ''}`}
              onClick={() => handleSelect(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
